export type TokenValidationMethod = "GET" | "POST";

export interface TokenValidationHttpResult {
  baseUrl: string;
  endpoint: string;
  requestedUrl: string;
  method: TokenValidationMethod;
  status: number | null;
  startedAt: number;
  finishedAt: number;
  durationMs: number;
  serverDate: string | null;
  wwwAuthenticate: string | null;
  networkError: string | null;
}

export interface JwtClaimDiagnostic {
  value: number | null;
  utc: string;
  local: string;
}

export interface JwtDiagnostic {
  tokenPresent: boolean;
  maskedToken: string;
  segmentCount: number;
  algorithm: string;
  formatValid: boolean;
  issues: string[];
  iat: JwtClaimDiagnostic;
  nbf: JwtClaimDiagnostic;
  exp: JwtClaimDiagnostic;
}

export interface TokenDiagnosticSummary {
  diagnosedAt: Date;
  environment: string;
  jwt: JwtDiagnostic;
  http: TokenValidationHttpResult;
  clockDifferenceMs: number | null;
  remainingSeconds: number | null;
  expired: boolean;
  notYetValid: boolean;
  classification: string;
  observations: string[];
}

const EMPTY_CLAIM: JwtClaimDiagnostic = {
  value: null,
  utc: "Não informado",
  local: "Não informado",
};

function maskToken(rawToken: string | null): string {
  if (!rawToken) return "Ausente";

  const token = rawToken.trim();
  if (!token) return "Ausente";

  const bearerMatch = token.match(/^Bearer\s+/i);
  const value = bearerMatch ? token.slice(bearerMatch[0].length) : token;
  const safeValue = value.replace(/[^A-Za-z0-9._-]/g, "?");

  if (value.length < 20) {
    return `[mascarado: ${value.length} caracteres]`;
  }

  return `${bearerMatch ? "Bearer " : ""}${safeValue.slice(0, 8)}…${safeValue.slice(-6)} (${value.length} caracteres)`;
}

function isValidBase64Url(value: string): boolean {
  if (!value || !/^[A-Za-z0-9_-]+$/.test(value) || value.length % 4 === 1) {
    return false;
  }

  try {
    const padding = "=".repeat((4 - (value.length % 4)) % 4);
    atob(value.replace(/-/g, "+").replace(/_/g, "/") + padding);
    return true;
  } catch {
    return false;
  }
}

function decodeBase64UrlJson(segment: string): Record<string, unknown> {
  if (!isValidBase64Url(segment)) {
    throw new Error("Base64Url inválido");
  }

  const padding = "=".repeat((4 - (segment.length % 4)) % 4);
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/") + padding;
  const binary = atob(base64);
  const bytes = Uint8Array.from(binary, (character) =>
    character.charCodeAt(0),
  );
  const decoded = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  const parsed = JSON.parse(decoded);

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Segmento JWT não contém um objeto JSON");
  }

  return parsed as Record<string, unknown>;
}

function parseNumericDate(value: unknown): JwtClaimDiagnostic {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return { ...EMPTY_CLAIM };
  }

  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) {
    return { ...EMPTY_CLAIM };
  }

  return {
    value,
    utc: date.toISOString(),
    local: date.toLocaleString(),
  };
}

export function analyzeJwt(rawToken: string | null): JwtDiagnostic {
  const issues: string[] = [];
  const tokenPresent = Boolean(rawToken?.trim());
  const maskedToken = maskToken(rawToken);

  if (!tokenPresent) {
    return {
      tokenPresent: false,
      maskedToken,
      segmentCount: 0,
      algorithm: "Não disponível",
      formatValid: false,
      issues: ["Token ausente."],
      iat: { ...EMPTY_CLAIM },
      nbf: { ...EMPTY_CLAIM },
      exp: { ...EMPTY_CLAIM },
    };
  }

  const original = rawToken ?? "";
  const trimmed = original.trim();

  if (original !== trimmed) {
    issues.push("O token contém espaços antes ou depois do valor.");
  }

  if (
    trimmed.startsWith('"') ||
    trimmed.endsWith('"') ||
    trimmed.startsWith("'") ||
    trimmed.endsWith("'")
  ) {
    issues.push("O token contém aspas indevidas.");
  }

  const bearerPrefixes = trimmed.match(/Bearer\s+/gi)?.length ?? 0;
  if (bearerPrefixes > 1 || /^Bearer\s+Bearer\s+/i.test(trimmed)) {
    issues.push("O prefixo Bearer está duplicado.");
  }

  const bearerMatch = trimmed.match(/^Bearer\s+/i);
  if (bearerMatch && bearerMatch[0] !== "Bearer ") {
    issues.push(
      "O prefixo Bearer possui capitalização ou espaçamento incompatível com o cliente HTTP atual.",
    );
  }
  const jwt = bearerMatch ? trimmed.slice(bearerMatch[0].length) : trimmed;

  if (/\s/.test(jwt)) {
    issues.push("O valor JWT contém espaços indevidos.");
  }

  const segments = jwt.split(".");
  if (segments.length !== 3) {
    issues.push(`O JWT possui ${segments.length} segmento(s), mas deveria possuir 3.`);
  }

  let header: Record<string, unknown> = {};
  let payload: Record<string, unknown> = {};

  if (segments.length === 3) {
    try {
      header = decodeBase64UrlJson(segments[0] ?? "");
    } catch {
      issues.push("O header possui Base64Url ou JSON inválido.");
    }

    try {
      payload = decodeBase64UrlJson(segments[1] ?? "");
    } catch {
      issues.push("O payload possui Base64Url ou JSON inválido.");
    }

    if (!isValidBase64Url(segments[2] ?? "")) {
      issues.push("A assinatura possui Base64Url inválido.");
    }
  }

  const algorithm =
    typeof header["alg"] === "string" && header["alg"].trim()
      ? header["alg"].replace(/[^A-Za-z0-9._+-]/g, "?").slice(0, 32)
      : "Não informado";

  return {
    tokenPresent,
    maskedToken,
    segmentCount: segments.length,
    algorithm,
    formatValid: issues.length === 0,
    issues,
    iat: parseNumericDate(payload["iat"]),
    nbf: parseNumericDate(payload["nbf"]),
    exp: parseNumericDate(payload["exp"]),
  };
}

export function estimateClockDifference(
  http: TokenValidationHttpResult,
): number | null {
  if (!http.serverDate) return null;

  const serverTime = Date.parse(http.serverDate);
  if (Number.isNaN(serverTime)) return null;

  const browserMidpoint = http.startedAt + http.durationMs / 2;
  return serverTime - browserMidpoint;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "Não disponível";

  const absoluteSeconds = Math.abs(Math.trunc(seconds));
  const days = Math.floor(absoluteSeconds / 86400);
  const hours = Math.floor((absoluteSeconds % 86400) / 3600);
  const minutes = Math.floor((absoluteSeconds % 3600) / 60);
  const remainingSeconds = absoluteSeconds % 60;
  const formatted = `${days}d ${hours}h ${minutes}m ${remainingSeconds}s`;

  return seconds < 0 ? `expirado há ${formatted}` : formatted;
}

export function formatClockDifference(milliseconds: number | null): string {
  if (milliseconds === null) return "Não disponível";

  const seconds = milliseconds / 1000;
  if (Math.abs(seconds) < 1) return "Menor que 1 segundo";

  const direction = seconds > 0 ? "servidor adiantado" : "servidor atrasado";
  return `${Math.abs(seconds).toFixed(1)}s (${direction} em relação ao navegador)`;
}

export function createDiagnosticSummary(
  jwt: JwtDiagnostic,
  http: TokenValidationHttpResult,
  environment: string,
): TokenDiagnosticSummary {
  const diagnosedAt = new Date();
  const clockDifferenceMs = estimateClockDifference(http);
  const referenceTimeMs =
    diagnosedAt.getTime() + (clockDifferenceMs === null ? 0 : clockDifferenceMs);
  const referenceTimeSeconds = referenceTimeMs / 1000;
  const remainingSeconds =
    jwt.exp.value === null ? null : jwt.exp.value - referenceTimeSeconds;
  const expired = remainingSeconds !== null && remainingSeconds <= 0;
  const notYetValid =
    jwt.nbf.value !== null && jwt.nbf.value > referenceTimeSeconds;
  const observations = [...jwt.issues];
  const hasAuthenticationDetails = Boolean(
    http.wwwAuthenticate &&
      /error|description|invalid|expired|signature/i.test(
        http.wwwAuthenticate,
      ),
  );

  let classification = "Diagnóstico inconclusivo porque o backend não retornou detalhes.";

  if (!jwt.tokenPresent || !jwt.formatValid) {
    classification = "Token ausente ou malformado.";
  } else if (http.status !== null && http.status >= 200 && http.status < 300) {
    classification = "Token aceito.";
  } else if (expired) {
    classification = "Token expirado.";
  } else if (notYetValid) {
    classification =
      "Token ainda não válido, provável diferença de relógio/fuso.";
  } else if (http.networkError) {
    classification = "Falha de rede, CORS, proxy ou URL incorreta.";
  } else if (http.status === 401 && hasAuthenticationDetails) {
    classification =
      "401 possivelmente relacionado à assinatura/configuração da API.";
  }

  if (http.status === 401 && !hasAuthenticationDetails) {
    observations.push(
      "A API retornou somente 401, sem detalhes expostos. O frontend não consegue confirmar a assinatura; será necessário habilitar diagnóstico seguro no middleware JWT do backend.",
    );
  } else if (http.status === 401) {
    observations.push(
      "O frontend não possui a chave secreta e não consegue confirmar se a assinatura JWT está correta.",
    );
  }

  if (http.status === 403) {
    observations.push(
      "A API retornou 403, mas não forneceu informação suficiente para distinguir autenticação de autorização.",
    );
  }

  if (http.networkError) {
    observations.push(http.networkError);
  }

  if (!http.serverDate) {
    observations.push(
      "O header Date não está disponível para o navegador; ele pode não ter sido retornado ou exposto pelo CORS/proxy.",
    );
  }

  if (!http.wwwAuthenticate && http.status === 401) {
    observations.push(
      "O header WWW-Authenticate não foi retornado ou não está exposto ao navegador.",
    );
  }

  return {
    diagnosedAt,
    environment,
    jwt,
    http,
    clockDifferenceMs,
    remainingSeconds,
    expired,
    notYetValid,
    classification,
    observations,
  };
}

function formatClaimForReport(claim: JwtClaimDiagnostic): string {
  if (claim.value === null) return "Não informado";
  return `${claim.value} | ${claim.utc} UTC | ${claim.local} local`;
}

export function buildDiagnosticReport(
  summary: TokenDiagnosticSummary,
): string {
  const { jwt, http } = summary;

  return [
    `Data/hora do diagnóstico: ${summary.diagnosedAt.toISOString()} UTC | ${summary.diagnosedAt.toLocaleString()} local`,
    `Ambiente: ${summary.environment}`,
    `Base URL: ${http.baseUrl}`,
    `Endpoint: ${http.requestedUrl}`,
    `Método HTTP: ${http.method}`,
    `Status HTTP: ${http.status ?? "Não disponível"}`,
    `Duração: ${http.durationMs}ms`,
    `Formato JWT: ${jwt.formatValid ? "Válido estruturalmente (assinatura não verificada)" : jwt.issues.join(" ")}`,
    `Token mascarado: ${jwt.maskedToken}`,
    `Quantidade de segmentos: ${jwt.segmentCount}`,
    `Algoritmo: ${jwt.algorithm}`,
    `Emitido em: ${formatClaimForReport(jwt.iat)}`,
    `Válido a partir de: ${formatClaimForReport(jwt.nbf)}`,
    `Expira em: ${formatClaimForReport(jwt.exp)}`,
    `Tempo restante: ${formatDuration(summary.remainingSeconds)}`,
    `Estado temporal: ${formatTemporalStatus(summary)}`,
    `Diferença navegador/servidor: ${formatClockDifference(summary.clockDifferenceMs)}`,
    `WWW-Authenticate: ${http.wwwAuthenticate ?? "Não disponível"}`,
    `Classificação: ${summary.classification}`,
    `Observações: ${summary.observations.length ? summary.observations.join(" ") : "Nenhuma observação adicional."}`,
  ].join("\n");
}

export function formatRemainingTime(seconds: number | null): string {
  return formatDuration(seconds);
}

export function formatTemporalStatus(
  summary: TokenDiagnosticSummary,
): string {
  if (summary.notYetValid) return "Ainda não válido";
  if (summary.expired) return "Expirado";
  if (summary.jwt.exp.value === null && summary.jwt.nbf.value === null) {
    return "Não determinado";
  }
  return "Dentro da janela temporal informada";
}

export function sanitizeDiagnosticUrl(value: string): string {
  try {
    const url = new URL(value);
    url.username = "";
    url.password = "";
    url.search = "";
    url.hash = "";
    return url.toString().replace(/\/$/, "");
  } catch {
    return value.split(/[?#]/, 1)[0] ?? "";
  }
}
